import { isNumber } from "@common/helpers/typeGuards";
import { currencyFormatter } from "@common/helpers/currency";
import { FINANCIAL_TYPES_AVAILABLE_FOR_RECONCILIATION } from "@common/helpers/reconciliation";
import {
    FinancialAccount,
    FinancialAccountSourceType,
    FinancialAccountType,
} from "@common/types/financialAccount";
import { FinancialConnection } from "@common/types/financialConnection";
import { isPlaidConnection } from "@common/helpers/financialConnection";
import { PlaidAccountMetadata } from "@common/helpers/plaid";
import { IntegrationAccount } from "@common/types/integrationAccount";
import { IntegrationConnection } from "@common/types/integrationConnection";
import { isIntegrationConnection } from "@common/helpers/integrationConnection";
import { isIncompleteStripeConnection } from "@common/helpers/stripeConnection";
import { getLeadingAccountConnection } from "@common/helpers/financialAccount";

export function displayAccountBalance(account: FinancialAccount) {
    return isNumber(account.balance)
        ? currencyFormatter.format(account.balance)
        : "-";
}

export const isCheckingAccount = (account: FinancialAccount) =>
    account.financialType === FinancialAccountType.CHECKING;

export function getIntegrationAccountOfConnection(
    financialAccount: FinancialAccount,
    connection: FinancialConnection | IntegrationConnection,
): IntegrationAccount | undefined {
    return financialAccount.integrationAccounts?.find(
        (integrationAccount) =>
            (integrationAccount.connectionId &&
                integrationAccount.connectionId === connection.id) ||
            (integrationAccount.integrationConnectionId &&
                integrationAccount.integrationConnectionId === connection.id),
    );
}

export function financialAccountIsImportOnly(
    financialAccount: FinancialAccount,
): boolean {
    const integrationAccounts = financialAccount.integrationAccounts ?? [];

    if (integrationAccounts.length > 0) {
        return integrationAccounts.every(
            (integrationAccount) =>
                integrationAccount.sourceType ===
                FinancialAccountSourceType.FILE_IMPORT,
        );
    }

    return (
        financialAccount.sourceType === FinancialAccountSourceType.FILE_IMPORT
    );
}

export function financialAccountIsErrored(
    financialAccount: FinancialAccount,
): boolean {
    const integrationAccountsWithConnection =
        financialAccount.integrationAccounts?.filter(
            (integrationAccount) =>
                integrationAccount.connection ??
                integrationAccount.integrationConnection,
        );

    if (
        !integrationAccountsWithConnection ||
        integrationAccountsWithConnection?.length === 0
    ) {
        return false;
    }

    // if at least one connection is healthy and can sync data consider the account healthy
    return integrationAccountsWithConnection.every((integrationAccount) =>
        integrationAccountHasError(
            integrationAccount,
            integrationAccount.connection ??
                integrationAccount.integrationConnection ??
                undefined,
        ),
    );
}

export function getDisconnectedReconcileConnection(
    financialAccount: FinancialAccount,
): {
    connection: FinancialConnection | IntegrationConnection;
    integrationAccount?: IntegrationAccount;
} | null {
    if (
        financialAccount.disabled ||
        !financialAccountIsErrored(financialAccount)
    ) {
        return null;
    }
    const connection = getLeadingAccountConnection(financialAccount);
    if (!connection) {
        return null;
    }
    const integrationAccount = financialAccount.integrationAccounts?.find(
        (ia) =>
            (ia.connection ?? ia.integrationConnection)?.id === connection.id,
    );
    return { connection, integrationAccount };
}

export function financialAccountHasIncompleteStripe(
    financialAccount: FinancialAccount,
): boolean {
    return (financialAccount.integrationAccounts ?? []).some(
        (integrationAccount) => {
            const connection =
                integrationAccount.connection ??
                integrationAccount.integrationConnection;
            return (
                connection != null && isIncompleteStripeConnection(connection)
            );
        },
    );
}

export const hasMatchingIntegrationAccount = ({
    financialAccount,
    connectionId,
}: {
    financialAccount: FinancialAccount;
    connectionId: string;
}) =>
    (financialAccount.integrationAccounts ?? []).some(
        (integrationAccount) =>
            integrationAccount.integrationConnectionId === connectionId,
    );

export const hasMatchingConnectionId = ({
    financialAccount,
    connectionId,
}: {
    financialAccount: FinancialAccount;
    connectionId: string;
}) =>
    (financialAccount.integrationAccounts ?? []).some(
        (integrationAccount) =>
            String(integrationAccount.connectionId) === connectionId,
    );

export function canReconcileFinancialAccount(
    account: FinancialAccount,
): boolean {
    return !getReconcileDisabledReason(account);
}

export function getReconcileDisabledReason(
    account: FinancialAccount,
): string | undefined {
    const isReconcilableType =
        FINANCIAL_TYPES_AVAILABLE_FOR_RECONCILIATION.includes(
            account.financialType,
        );
    if (!isReconcilableType) {
        return "Reconciliation is only available for checking, savings, and credit card accounts";
    }
    return undefined;
}

export function integrationAccountHasError(
    integrationAccount: IntegrationAccount,
    connection?: FinancialConnection | IntegrationConnection,
): boolean {
    if (!connection) {
        return false;
    }

    if (connection.error) {
        return true;
    }

    if (!isIntegrationConnection(connection) && isPlaidConnection(connection)) {
        return Boolean(
            integrationAccount.metadata?.[PlaidAccountMetadata.ACCOUNT_CHURN],
        );
    }

    return false;
}
